import type { Dispatch, SetStateAction } from "react";

import type { TailorResumePayload } from "../../../__generated__/types.gqlgenerated";
import type { ResumeOriginalContentFieldsFragment } from "../ResumeGlowup/graphql/__generated__/resumeOriginalContent.gqlgenerated";

/** Lifecycle of converting a PARSED resume and applying its approved Glow Up changes. */
export type ParsedResumeConversion =
  | {
      /** The conversion returned the exact Indeed Resume to use as the save target. */
      status: "indeedResumeReadyToSave";
      sourceResumeId: string;
      indeedResume: ResumeOriginalContentFieldsFragment;
    }
  | {
      /** All approved Glow Up changes were successfully applied to the Indeed Resume. */
      status: "suggestionsApplied";
      sourceResumeId: string;
      indeedResume: ResumeOriginalContentFieldsFragment;
    };

/**
 * Data tracked for Resume Glowup routing and state management
 */
export interface ResumeGlowupData {
  /**
   * Whether the user has opted in to review their resume.
   * When true, the review page will be added to the route.
   */
  hasOptedIn: boolean;
  // The original resume data fetched — exactly the fields ResumeOriginalContent selects.
  originalResume?: ResumeOriginalContentFieldsFragment;
  /**
   * Conversion state for a PARSED upload. It retains enough progress to retry
   * readback or saving without converting the same resume again.
   */
  parsedResumeConversion?: ParsedResumeConversion;
  // The AI suggestions for the resume sections and rewrite reasons
  aiImprovements?: TailorResumePayload;
  // The user's decisions on the sections
  sectionDecisions: {
    summary: SectionDecision;
    skills: SectionDecision;
    workExperiences: Record<string, SectionDecision>;
  };
  // The status of the fetch operations
  fetchStatus: {
    originalResume: FetchStatus;
    aiSuggestions: FetchStatus;
  };
  deletedOriginalSkillIds: string[];
  userAddedSkills: Array<{ text: string }>;
  /** User-edited work experience fields keyed by work experience ID. Survives AI re-generation. */
  userEditedWorkExperiences: Record<string, WorkExperienceEdit>;
  userAddedWorkExperiences: Array<WorkExperienceEdit & { id: string }>;
}

export interface WorkExperienceEdit {
  title: string;
  company: string;
  /** Plain text — saved as `{ unknownLocation }` in the mutation. */
  location: string;
  dateRange: {
    fromDate?: { month?: string | null; year?: number | null };
    toDate?: { month?: string | null; year?: number | null };
    isCurrent?: boolean | null;
  };
  description: string;
}

export interface ResumeGlowupDataContextType {
  resumeGlowupData: ResumeGlowupData;
  setResumeGlowupData: Dispatch<SetStateAction<ResumeGlowupData>>;
  /**
   * Determines if the Resume Glowup Edit/Restore proctor test
   * (jso_resume_glowup_edit_tst) is enabled.
   */
  isEditFlowEnabled: boolean;
}

/** User's decision on a section enhancement */
export enum SectionDecision {
  Pending = "pending",
  Approved = "approved",
  Dismissed = "dismissed",
}

/** Status of individual data fetches */
export enum FetchStatus {
  Pending = "pending",
  Success = "success",
  Error = "error",
  Timeout = "timeout",
}
